import axios from 'axios';
import AuthService from '../services/auth.service'

const moment = require('moment')
require('moment/locale/pt')

const API_URL = process.env.VUE_APP_API_URL;
const ADMIN_URL = process.env.VUE_APP_ADMIN_URL;
const GRAPHQL_URL = process.env.VUE_APP_GRAPHQL_URL;
const requestHeaders = {
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
};

class ApiService {

  constructor() {
    axios.defaults.headers['Accept'] = 'application/json';

    // Request Interceptor
    axios.interceptors.request.use(config => {
      config.headers['Content-Type'] = 'application/json';
      config.headers['Authorization'] = 'Bearer ' + localStorage.getItem('token');
      return config;
    }, error => {
      // handle the error
      return Promise.reject(error);
    });

    // Response Interceptor
    axios.interceptors.response.use((response) => {
      return response;
    }, error => {
      // handle the response error
      const originalRequest = error.config;

      if (error.response.status === 401 && !originalRequest._retry) {
        if (error.config.url.includes('https://securetoken.googleapis.com/v1/token')) {
          AuthService.logout();
          return Promise.reject(error);
        } else {
          originalRequest._retry = true;
          return AuthService.getNewToken()?.then((token) => {
            localStorage.setItem('token', token);

            return axios(originalRequest);
          });
        }

      }

      // return Error object with Promise
      return Promise.reject(error);
    });
  }

  /** BANK */
  getBankStats(isBackoffice: boolean) {
    if (isBackoffice) {
      return axios.get(ADMIN_URL + '/bank/stats');
    } else {
      return axios.get(API_URL + '/bank/stats');
    }
  }

  getMonthlyBankStats(isBackoffice: boolean) {
    if (isBackoffice) {
      return axios.get(ADMIN_URL + '/bank/monthly-stats');
    } else {
      return axios.get(API_URL + '/bank/monthly-stats');
    }
  }

  createBank(isBackoffice: boolean, amount: number) {
    if (isBackoffice) {
      return axios.post(ADMIN_URL + '/bank/create/' + amount);
    } else {
      return axios.post(API_URL + '/bank/create/' + amount);
    }
  }

  resetBank(isBackoffice: boolean) {
    if (isBackoffice) {
      return axios.delete(ADMIN_URL + '/bank/reset');
    } else {
      return axios.delete(API_URL + '/bank/reset');
    }
  }

  /** BIGOMETRO */
  submitBigometro(prognostic: any) {
    const data = this.getDataFromPrognostic(prognostic);
    return axios.post(API_URL + '/bigometro', data);
  }

  removeBigometroBet(prognosticId: string) {
    return axios.delete(API_URL + '/bigometro/' + prognosticId);
  }

  /** USERS */
  
  getGraphQLUserStatsCurrentTiroCerteiro() {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query { 
          getCurrentTiroCerteiro {
            id,
            startDate
            endDate
            prize
            description
          },
          getUserStats {
            registered {
              count
            }
            free {
              count
            }
            active {
              count
            }
            subscriptionStats {
              app
              paymentType
              count
              month
              year
            }
            registrationStats {
              count
              month
              year
            }
          }
        }`
      }
    });
  }

  getGraphQLUserStatsCurrentTiroCerteiroWithPeriod(period: number) {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getUserStats($period: String) { 
          getCurrentTiroCerteiro {
            id,
            startDate
            endDate
            prize
            description
          },
          getUserStats(period: $period) {
            registered {
              count
            }
            free {
              count
            }
            active {
              count
            }
            subscriptionStats {
              app
              paymentType
              count
              day
              month
              year
            }
            registrationStats {
              count
              day
              month
              year
            }
          }
        }`,
        operationName: "getUserStats",
        variables: {
          period: period
        }
      }
    });
  }

  getUser() {
    return axios.get(API_URL + '/user');
  }

  getAdminUsers(search: string, page: number, size: number) {
    search = search ? search : "";
    return axios.get(ADMIN_URL + '/users?text=' + search + '&page=' + page + '&size=' + size);
  }

  updateUser(user: any) {
    return axios.put(API_URL + '/user', user);
  }

  updatePicture(picture: string) {
    return axios.put(API_URL + '/user/picture', picture);
  }

  deactivateUser() {
    return axios.put(API_URL + '/user/deactivate');
  }

  deleteUser(userId: string) {
    return axios.delete(ADMIN_URL + '/user/' + userId);
  }

  updateAdminUser(user: any) {
    return axios.put(ADMIN_URL + '/user', user);
  }

  registerUser(email: string, name: string, phone: string) {
    const data = {
      email: email,
      name: name,
      phone: phone
    };
    return axios.post(API_URL + '/user', data);
  }

  getAlerts(page: number, size: number) {
    return axios.get(API_URL + '/user/alerts?page=' + page + '&size=' + size);
  }

  markAlertMessageAsRead(alertId: string, hash: string) {
    return axios.put(API_URL + '/user/alert/' + alertId + '/read', hash);
  }

  /** */

  getPrognostics(page: number, size: number) {
    return axios.get(API_URL + '/prognostics?page=' + page + '&size=' + size);
  }

  getGraphQLCompetitions(page: number, size: number) {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getCompetitions ($page: Int, $size: Int) {
          getCompetitions (page: $page, size: $size) {
            competitions {
              id
              name
              description
              country {
                  id
                  name
              }
              sport {
                  id
                  name
              }
            }
            totalElements
            totalPages
          }
        }`,
        operationName: "getCompetitions",
        variables: {
          page: page,
          size: size
        }
      }
    });
  }

  getGraphQLMarkets(page: number, size: number, search: string) {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getMarkets ($page: Int, $size: Int, $search: String) {
          getMarkets (page: $page, size: $size, search: $search) {
            markets {
              id
              name
              description
              sport {
                  id
                  name
              }
              outcomes
            }
            totalElements
            totalPages
          }
        }`,
        operationName: "getMarkets",
        variables: {
          page: page,
          size: size,
          search: search
        }
      }
    });
  }

  getGraphQLCompetitionList(country: any, sport: any) {
    const countryId = country.id ? country.id : country;
    const sportId = sport.id ? sport.id : sport;

    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getCompetitionsByCountryAndSport ($countryId: String, $sportId: String) {
          getCompetitionsByCountryAndSport (countryId: $countryId, sportId: $sportId) {
            id,
            name
            country {
              id
              name
            }
            sport {
              id
              name
            }
          }
        }`,
        operationName: "getCompetitionsByCountryAndSport",
        variables: {
          countryId: countryId,
          sportId: sportId
        }
      }
    });
  }

  getGraphQLFrontendLists() {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query { 
          getCountriesAndSports {  
            sports { 
              id 
              name 
            } 
            countries { 
              id 
              name 
            }
          }
        }`
      }
    });
  }

  /** BILLING PLANS */
  getBillingPlans(country: string) {
    console.log(country)
    if(country == "Portugal") {
      return axios.get(API_URL + '/billing/portuguese-plans');
    } else {
      return axios.get(API_URL + '/billing/international-plans');
    }
  }

  getFullBillingPlans() {
    return axios.get(API_URL + '/billing/plans/full');
  }

  getBillingPlanById(planId: string) {
    return axios.get(ADMIN_URL + '/billing/plans/' + planId);
  }

  /** MATCHES */

  getMatches(search: string, page: number, size: number) {
    search = search ? search : "";
    return axios.get(ADMIN_URL + '/matches?text=' + search + '&page=' + page + '&size=' + size);
  }

  getGraphQLMatches(page: number, size: number) {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getMatches ($page: Int, $size: Int) {
          getMatches (page: $page, size: $size) {
            matches {
                id
                date
                hour
                country {
                    id
                    name
                }
                sport {
                    id
                    name
                }
                competition {
                  id
                  name
                }
                homeTeam {
                  id
                  name
                  image
                }
                awayTeam {
                  id
                  name
                  image
                }
                halfTime
            },
            totalElements
            totalPages
          }
        }`,
        operationName: "getMatches",
        variables: {
          page: page,
          size: size
        }
      }
    });
  }

  getGraphQLMatchesList(competition: any) {
    const competitionId = competition.id ? competition.id : competition;

    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getMatchesByCompetition ($competitionId: String) {
          getMatchesByCompetition (competitionId: $competitionId) {
            id,
            date
            hour
            competition {
              id
              name
            }
            homeTeam {
              id
              name
            }
            awayTeam {
              id
              name
            }
          }
        }`,
        operationName: "getMatchesByCompetition",
        variables: {
          competitionId: competitionId,
        }
      }
    });
  }

  saveMatch(match: any) {
    const data = {
      id: match.id,
      date: match.date,
      hour: match.hour,
      countryId: match.country.id ? match.country.id : match.country,
      sportId: match.sport.id ? match.sport.id : match.sport,
      competitionId: match.competition.id ? match.competition.id : match.competition,
      homeTeamId: match.homeTeam.id ? match.homeTeam.id : match.homeTeam,
      awayTeamId: match.awayTeam.id ? match.awayTeam.id : match.awayTeam,
      halfTime: match.halfTime
    };
    return axios.post(ADMIN_URL + '/match', data);
  }

  deleteMatch(matchId: string) {
    return axios.delete(ADMIN_URL + '/match/' + matchId);
  }

  /** PROGNOSTICS */

  getAdminPrognostics(search: string, page: number, size: number) {
    return axios.get(ADMIN_URL + '/prognostics?text=' + search + '&page=' + page + '&size=' + size);
  }

  getGraphQLPrognostics(page: number, size: number, filter: string, type: string, status: string) {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getPrognostics ($page: Int, $size: Int, $filter: String, $type: String, $status: String) {
            getPrognostics (page: $page, size: $size, filter: $filter, type: $type, status: $status) {
              prognostics {
                id
                matchMarkets {
                  match {
                    id
                    date
                    hour
                    sport {
                      id
                      name
                    }
                    country {
                      id
                      name
                    }
                    competition {
                      id
                      name
                    }
                    homeTeam {
                      id
                      name
                      image
                    }
                    awayTeam {
                      id
                      name
                      image
                    }
                    halfTime
                  }
                  tiroCerteiroMarkets {
                    name
                    outcomes {
                      name
                      result
                      percentage
                      users {
                        uid
                        photo
                      }
                      userIds
                    }
                  }
                  bigoddsMarkets {
                    market {
                      id
                      name
                      outcomes
                    }
                    outcome
                    odd
                    bank
                    result
                  }
                }
                odd
                bank
                tiroCerteiro
                status
                bigometroBets {
                  userId
                  whileOpen
                }
              }
              totalElements
              totalPages
            }
          }`,
        operationName: "getPrognostics",
        variables: {
          page: page,
          size: size,
          filter: filter,
          type: type,
          status: status
        }
      }
    });
  }

  getAdminGraphQLPrognostics(search: string, page: number, size: number) {
    search = search ? search : "";
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getPrognosticsByText ($text: String, $page: Int, $size: Int) {
          getPrognosticsByText (text: $text, page: $page, size: $size) {
              prognostics {
                id
                matchMarkets {
                  match {
                    id
                    date
                    hour
                    sport {
                      id
                      name
                    }
                    country {
                      id
                      name
                    }
                    competition {
                      id
                      name
                    }
                    homeTeam {
                      id
                      name
                      image
                    }
                    awayTeam {
                      id
                      name
                      image
                    }
                    halfTime
                  }
                  tiroCerteiroMarkets {
                    name
                    outcomes {
                      name
                      result
                      percentage
                      users {
                        uid
                        photo
                      }
                      userIds
                    }
                  }
                  bigoddsMarkets {
                    market {
                      id
                      name
                      outcomes
                    }
                    outcome
                    odd
                    bank
                    result
                  }
                }
                odd
                bank
                tiroCerteiro
                status
                bigometroBets {
                  userId
                  whileOpen
                }
              }
              totalElements
              totalPages
            }
          }`,
        operationName: "getPrognosticsByText",
        variables: {
          text: search,
          page: page,
          size: size
        }
      }
    });
  }

  getDataFromPrognostic(prognostic: any) {

    const data = {
      id: prognostic.id,
      matchMarkets: prognostic.matchMarkets,
      odd: prognostic.odd,
      bank: prognostic.bank,
      tiroCerteiro: prognostic.tiroCerteiro,
      status: prognostic.status,
      bigometroBets: prognostic.bigometroBets
    };

    for (let i = 0; i < prognostic.matchMarkets.length; i++) {
      data.matchMarkets[i].matchId = prognostic.matchMarkets[i].match.id;
      for (let j = 0; j < prognostic.matchMarkets[i].bigoddsMarkets.length; j++) {
        data.matchMarkets[i].bigoddsMarkets[j].marketId = prognostic.matchMarkets[i].bigoddsMarkets[j].market.id;
      }
    }

    return data;
  }

  savePrognoctic(prognostic: any) {
    const data = this.getDataFromPrognostic(prognostic);
    return axios.post(ADMIN_URL + '/prognostic', data);
  }

  closePrognoctic(prognostic: any) {
    const data = this.getDataFromPrognostic(prognostic);
    return axios.post(ADMIN_URL + '/prognostic/close', data);
  }

  updatePrognoctic(prognostic: any) {
    const data = this.getDataFromPrognostic(prognostic);
    return axios.put(API_URL + '/prognostic', data);
  }

  deletePrognostic(prognosticId: string) {
    return axios.delete(ADMIN_URL + '/prognostic/' + prognosticId);
  }

  getGraphQLComments(prognosticId: string, page: number, size: number) {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getComments ($prognosticId: String, $page: Int, $size: Int) {
          getComments (prognosticId: $prognosticId, page: $page, size: $size) {
              comments {
                id
                user {
                  uid
                  photo
                  name
                }
                message
                date
              }
              totalElements
              totalPages
            }
          }`,
        operationName: "getComments",
        variables: {
          prognosticId: prognosticId,
          page: page,
          size: size
        }
      }
    });
  }

  addComment(prognosticId: string, message: string) {
    return axios.post(API_URL + '/prognostic/' + prognosticId + '/comment', message);
  }

  removeComment(prognosticId: string, commentId: string) {
    return axios.delete(API_URL + '/prognostic/' + prognosticId + '/comment/' + commentId);
  }

  /** TIRO CERTEIRO */

  saveTiroCerteiro(tiroCerteiro: any) {
    const data = {
      id: tiroCerteiro.id,
      startDate: moment(tiroCerteiro.startDate).valueOf(),
      endDate: moment(tiroCerteiro.endDate).valueOf(),
      prize: tiroCerteiro.prize,
      description: tiroCerteiro.description
    };
    return axios.post(ADMIN_URL + '/tiro-certeiro', data);
  }

  getGraphQLCurrentTiroCerteiro() {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query { 
          getCurrentTiroCerteiro {
            id,
            startDate
            endDate
            prize
            description
          }
        }`
      }
    });
  }

  getGraphQLCurrentTiroCerteiroRanking() {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query { 
          getCurrentTiroCerteiro {
            id,
            startDate
            endDate
            prize
            description
          }
          getCurrentTiroCerteiroRanking {
            user {
              uid
              photo
              name
            }
            greens
            reds
            voids
            points
            totalBets
            prognostics {
              status
            }
          }
        }`
      }
    });
  }
  getGraphQLCerteiroRankingById(id: string) {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getTiroCerteiroRankingById ($id: String) {
          getTiroCerteiroRankingById (id: $id) {
            user {
              uid
              photo
              name
            }
            greens
            reds
            voids
            points
            totalBets
            prognostics {
              status
            }
          }
        }`,
        operationName: "getTiroCerteiroRankingById",
        variables: {
          id: id,
        }
      }
    });
  }

  getGraphQLTirosCerteiros() {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query { 
          getCurrentTiroCerteiro {
            id,
            startDate
            endDate
            prize
            description
          }
          getNextTiroCerteiro {
            id,
            startDate
            endDate
            prize
            description
          }
          getPreviousTiroCerteiro {
            id,
            startDate
            endDate
            prize
            description
          }
        }`
      }
    });
  }

  deleteTiroCerteiro(tiroCerteiroId: string) {
    return axios.delete(ADMIN_URL + '/tiro-certeiro/' + tiroCerteiroId);
  }

  /** INVOICES */
  getInvoices(search: string, page: number, size: number) {
    return axios.get(ADMIN_URL + '/invoiceexpress/list?text=' + search + '&page=' + page + '&size=' + size);
  }

  getInvoicePdf(invoiceId: string) {
    return axios.get(ADMIN_URL + '/invoiceexpress/pdf?invoiceId=' + invoiceId);
  }

  /** NEWS */
  getNews(search: string, page: number, size: number) {
    return axios.get(API_URL + '/news?text=' + search + '&page=' + page + '&size=' + size);
  }

  saveNews(news: any) {
    return axios.post(ADMIN_URL + '/news', news);
  }

  deleteNews(newsId: string) {
    return axios.delete(ADMIN_URL + '/news/' + newsId);
  }

  /** SPORTS */

  getSports(search: string, page: number, size: number) {
    return axios.get(ADMIN_URL + '/sports?text=' + search + '&page=' + page + '&size=' + size);
  }

  saveSport(sport: any) {
    return axios.post(ADMIN_URL + '/sport', sport);
  }

  deleteSport(sportName: string) {
    return axios.delete(ADMIN_URL + '/sport/' + sportName);
  }

  /** COUNTRIES */

  getCountries(search: string, page: number, size: number) {
    search = search ? search : "";
    return axios.get(ADMIN_URL + '/countries?text=' + search + '&page=' + page + '&size=' + size);
  }

  saveCountry(country: any) {
    return axios.post(ADMIN_URL + '/country', country);
  }

  deleteCountry(countryName: string) {
    return axios.delete(ADMIN_URL + '/country/' + countryName);
  }

  /** COMPETITIONS */

  getCompetitions(search: string, page: number, size: number) {
    search = search ? search : "";
    return axios.get(ADMIN_URL + '/competitions?text=' + search + '&page=' + page + '&size=' + size);
  }

  getCompetitionList(country: string, sport: string) {
    return axios.get(ADMIN_URL + '/competitions/list?country=' + country + '&sport=' + sport);
  }

  saveCompetition(competition: any) {
    const data = {
      id: competition.id,
      name: competition.name,
      description: competition.description,
      countryId: competition.country.id ? competition.country.id : competition.country,
      sportId: competition.sport.id ? competition.sport.id : competition.sport
    };
    return axios.post(ADMIN_URL + '/competition', data);
  }

  deleteCompetition(competitionName: string) {
    return axios.delete(ADMIN_URL + '/competition/' + competitionName);
  }

  /** TEAMS/PLAYER */

  getTeamsPlayers(search: string, page: number, size: number) {
    search = search ? search : "";
    return axios.get(ADMIN_URL + '/teams-players?text=' + search + '&page=' + page + '&size=' + size);
  }

  getGraphQLTeamsPlayers(page: number, size: number) {
    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getTeams ($page: Int, $size: Int) {
          getTeams (page: $page, size: $size) {
            teamsPlayers {
                id
                name
                description
                country {
                    id
                    name
                }
                sport {
                    id
                    name
                }
                competition {
                  id
                  name
                }
                image
            },
            totalElements
            totalPages
          }
        }`,
        operationName: "getTeams",
        variables: {
          page: page,
          size: size
        }
      }
    });
  }

  getTeamsPlayersList(competition: string) {
    return axios.get(ADMIN_URL + '/teams-players/list?competition=' + competition);
  }

  getGraphQLTeamsPlayersList(competition: any) {
    const competitionId = competition.id ? competition.id : competition;

    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getTeamsByCompetition ($competitionId: String) {
          getTeamsByCompetition (competitionId: $competitionId) {
            id,
            name
            country {
              id
              name
            }
            sport {
              id
              name
            }
          }
        }`,
        operationName: "getTeamsByCompetition",
        variables: {
          competitionId: competitionId,
        }
      }
    });
  }

  saveTeamPlayer(teamPlayer: any) {
    const data = {
      id: teamPlayer.id,
      name: teamPlayer.name,
      description: teamPlayer.description,
      countryId: teamPlayer.country.id ? teamPlayer.country.id : teamPlayer.country,
      sportId: teamPlayer.sport.id ? teamPlayer.sport.id : teamPlayer.sport,
      competitionId: teamPlayer.competition.id ? teamPlayer.competition.id : teamPlayer.competition,
      image: teamPlayer.image
    };
    return axios.post(ADMIN_URL + '/team-player', data);
  }

  deleteTeamPlayer(teamPlayerName: string) {
    return axios.delete(ADMIN_URL + '/team-player/' + teamPlayerName);
  }

  /** MARKETS */

  getMarkets(search: string, page: number, size: number) {
    search = search ? search : "";
    return axios.get(ADMIN_URL + '/markets?text=' + search + '&page=' + page + '&size=' + size)
  }

  getGraphQLMarketsList(sport: any) {
    const sportId = sport.id ? sport.id : sport;

    return axios({
      url: GRAPHQL_URL,
      method: 'post',
      data: {
        query: `query getMarketsBySportId ($sportId: String) {
          getMarketsBySportId (sportId: $sportId) {
            id,
            name
            description
            sport {
              id
              name
            }
            outcomes
          }
        }`,
        operationName: "getMarketsBySportId",
        variables: {
          sportId: sportId,
        }
      }
    });
  }

  saveMarket(market: any) {
    const data = {
      id: market.id,
      name: market.name,
      description: market.description,
      sportId: market.sport.id ? market.sport.id : market.sport,
      outcomes: market.outcomes
    };
    return axios.post(ADMIN_URL + '/market', data);
  }

  deleteMarket(marketName: string) {
    return axios.delete(ADMIN_URL + '/market/' + marketName);
  }

  /** WEBSITE */

  getMembers(page: number, size: number) {
    return axios.get(ADMIN_URL + '/team-members?page=' + page + '&size=' + size)
  }

  getStatistics(page: number, size: number) {
    return axios.get(ADMIN_URL + '/statistics?page=' + page + '&size=' + size)
  }

  getCharts(page: number, size: number) {
    return axios.get(ADMIN_URL + '/charts?page=' + page + '&size=' + size)
  }

  getSubscriptions(page: number, size: number) {
    return axios.get(ADMIN_URL + '/subscriptions?page=' + page + '&size=' + size)
  }

  saveMember(member: any) {
    return axios.post(ADMIN_URL + '/team-member', member);
  }

  saveStatistic(statistic: any) {
    return axios.post(ADMIN_URL + '/statistic', statistic);
  }

  saveChart(chart: any) {
    return axios.post(ADMIN_URL + '/chart', chart);
  }

  saveSubscription(subscription: any) {
    return axios.post(ADMIN_URL + '/subscription', subscription);
  }

  deleteMember(memberId: string) {
    return axios.delete(ADMIN_URL + '/team-member/' + memberId);
  }

  deleteStatistic(statisticId: string) {
    return axios.delete(ADMIN_URL + '/statistic/' + statisticId);
  }

  deleteChart(chartId: string) {
    return axios.delete(ADMIN_URL + '/chart/' + chartId);
  }

  deleteSubscription(subscriptionId: string) {
    return axios.delete(ADMIN_URL + '/subscription/' + subscriptionId);
  }

  /** SUBSCRIPTIONS */
  addSubscriptionPlan(subscriptionId: string, planId: string) {
    const data = {
      subscriptionId: subscriptionId,
      planId: planId
    };
    return axios.post(API_URL + '/user/payment', data);
  }

  cancelSubscription(subscriptionId: string) {
    return axios.delete(API_URL + '/subscription/' + subscriptionId);
  }

  /** HELPER */

  getFrontendLists() {
    return axios.get(ADMIN_URL + '/frontend/lists');
  }

}

export default new ApiService();